import type { NavItem } from "../../api/shellApi";

/**
 * How the server's permission-aware menu (GET /navigation) is laid out in the new shell.
 * The menu itself is never recomputed here: items the user has no permission for are not in the
 * list, and anything not placed in a group still shows under "עוד" (never lose a screen).
 */
export type FlatNavItem = { key: string; label: string; path: string; parentKey?: string };

export const NAV_ICONS: Record<string, string> = {
  home: "home",
  organizations: "building",
  customers: "users",
  subscriptions: "repeat",
  finance_structure: "folder",
  income_expenses: "wallet",
  "income_expenses.bank": "bank",
  "income_expenses.suppliers": "box",
  "income_expenses.receipts": "receipt",
  "income_expenses.accounting": "file",
  "income_expenses.payment_requests": "card",
  "income_expenses.documents": "folder",
  users: "users",
  tasks: "task",
  meetings: "calendar",
  personal_area: "user",
  org_guidance: "book",
  mail_log: "inbox",
  mailing: "mail",
  forms: "note",
  automations: "sparkle",
  ai_data_entry: "upload",
  ai_data_audit: "check",
  settings: "gear",
  "settings.permissions": "key",
  chat: "reply",
  sales_home: "chart",
  sales_leads: "phone",
  sales_deals: "receipt",
  sales_collections: "wallet",
  sales_listens: "mic",
  sales_sends: "history",
  sales_commissions: "coin",
  sales_settings: "sliders",
};

export type NavGroupDef = { key: string; title?: string; ids: string[] };

/** Shared top of the sidebar — the same in every module. */
export const NAV_TOP_IDS = ["home", "organizations"];

export type NavModuleKey = "sales" | "finance";

/**
 * Work modules in the top bar (like Turbo's «תורמים» / «כספים») — each one has its own sidebar groups and colour.
 * Virtual modules over the existing routes: no route or permission changes. `ids` are nav keys
 * (a key may come from a child item). A group titled "@customers" takes the tenant's customers label.
 */
export const NAV_MODULES: Array<{ key: NavModuleKey; label: string; color: string; home: string; groups: NavGroupDef[] }> = [
  {
    key: "sales",
    label: "מכירות",
    color: "#4F46E5",
    home: "sales_home",
    groups: [
      { key: "s-sales", title: "מכירות", ids: ["sales_home", "sales_leads", "sales_deals", "sales_collections"] },
      { key: "s-line", title: "קו וצוות", ids: ["sales_listens", "sales_sends", "sales_commissions", "sales_settings"] },
      { key: "s-customers", title: "@customers", ids: ["customers"] },
      { key: "s-billing", title: "מנויים וגבייה", ids: ["subscriptions"] },
    ],
  },
  {
    key: "finance",
    label: "כספים",
    color: "#059669",
    home: "income_expenses",
    groups: [
      { key: "f-tx", title: "תנועות", ids: ["income_expenses"] },
      {
        key: "f-mgmt",
        title: "ניהול כספי",
        ids: ["income_expenses.bank", "income_expenses.suppliers", "income_expenses.payment_requests"],
      },
      {
        key: "f-docs",
        title: "מסמכים",
        ids: ["income_expenses.receipts", "income_expenses.accounting", "income_expenses.documents"],
      },
      { key: "f-more", ids: ["finance_structure"] },
    ],
  },
];

/** Every key placed in a module or in the shared top. */
export const NAV_MODULE_IDS: string[] = [...NAV_TOP_IDS, ...NAV_MODULES.flatMap((m) => m.groups.flatMap((g) => g.ids))];

/** The module a nav key belongs to (null = shared / office / settings). */
export function navModuleOf(key: string | null | undefined): NavModuleKey | null {
  if (!key) return null;
  return NAV_MODULES.find((m) => m.groups.some((g) => g.ids.includes(key)))?.key ?? null;
}

export type ModuleNav = {
  key: NavModuleKey;
  label: string;
  color: string;
  /** where switching to the module lands */
  homePath: string;
  groups: Array<{ key: string; title?: string; items: FlatNavItem[] }>;
};

/** The modules this user has screens in (empty groups and empty modules are dropped). */
export function buildModuleNav(byKey: Map<string, FlatNavItem>, customersLabel: string): ModuleNav[] {
  const pick = (ids: string[]) => ids.map((id) => byKey.get(id)).filter((x): x is FlatNavItem => Boolean(x));
  return NAV_MODULES.map((m) => {
    const groups = m.groups
      .map((g) => ({ key: g.key, title: g.title === "@customers" ? customersLabel : g.title, items: pick(g.ids) }))
      .filter((g) => g.items.length);
    const first = byKey.get(m.home) ?? groups[0]?.items[0];
    return { key: m.key, label: m.label, color: m.color, homePath: first?.path ?? "/", groups };
  }).filter((m) => m.groups.length);
}

/** The fixed "משרד" group — the same in every area of the system. */
export const OFFICE_IDS = [
  "tasks",
  "meetings",
  "forms",
  "automations",
  "mailing",
  "mail_log",
  "org_guidance",
  "ai_data_entry",
  "ai_data_audit",
];

/** Settings live in one hub (the "הגדרות" item at the bottom of the sidebar opens it). */
export const SETTINGS_IDS = ["settings", "settings.permissions", "users"];

/** Moved to the personal menu (bottom of the sidebar). */
export const PERSONAL_ID = "personal_area";

/** Not a screen in the new shell — the chat is the floating button. */
export const HIDDEN_IDS = new Set(["chat"]);

/** Shorter labels where the server's label is long for a sidebar. */
export const NAV_LABELS: Record<string, string> = {
  income_expenses: "הכנסות והוצאות",
  "income_expenses.bank": "התאמות בנק",
};

export function flattenNav(items: NavItem[], customersLabel: string): FlatNavItem[] {
  const out: FlatNavItem[] = [];
  const add = (it: NavItem, parentKey?: string) => {
    if (HIDDEN_IDS.has(it.key)) return;
    const label = it.key === "customers" ? customersLabel : NAV_LABELS[it.key] || it.labelHe;
    out.push({ key: it.key, label, path: it.path, parentKey });
    (it.children || []).forEach((c) => add(c, it.key));
  };
  items.forEach((it) => add(it));
  // the permissions screen is a sub-page of settings (no own menu entry on the server)
  if (out.some((it) => it.key === "settings")) {
    out.push({ key: "settings.permissions", label: "הרשאות ותבניות תפקיד", path: "/settings/permissions", parentKey: "settings" });
  }
  return out;
}

export function isActivePath(pathname: string, path: string): boolean {
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** The most specific nav item for a path (longest matching prefix). */
export function currentNavItem(pathname: string, items: FlatNavItem[]): FlatNavItem | null {
  let best: FlatNavItem | null = null;
  items.forEach((it) => {
    if (isActivePath(pathname, it.path) && (!best || it.path.length > best.path.length)) best = it;
  });
  return best;
}
